import Axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

export interface IError {
    [key: string]: string[];
}

export interface AxiosApiResponse<T> extends ApiResponse<T> {
    success: boolean;
    errors?: IError;
    emailVerificationNeeded?: boolean;
    smsVerificationNeeded?: boolean;
    emailCodeExpired?: boolean;
    smsCodeExpired?: boolean;
}

export interface ApiResponse<T> {
    data: T;
    redirectTo?: string;
    totalCount?: number;
    pageCount?: number;
    currentPage?: number;
    passwordResetToken?: string;
}

export interface ApiErrorParams {
    errors?: IError;
    emailVerificationNeeded?: boolean;
    smsVerificationNeeded?: boolean;
    emailCodeExpired?: boolean;
    smsCodeExpired?: boolean;
}

export type HttpHeaders = {
    [key: string]: string;
};

export interface IApiClient {
    post<TRequest, TResponse>(path: string, object: TRequest): Promise<ApiResponse<TResponse>>;
    delete<TResponse>(path: string): Promise<ApiResponse<TResponse>>;
    put<TRequest, TResponse>(path: string, object: TRequest): Promise<ApiResponse<TResponse>>;
    get<TResponse>(path: string): Promise<ApiResponse<TResponse>>;
}

export class ApiError extends Error {
    public errors?: IError = {};
    public emailVerificationNeeded = false;
    public smsVerificationNeeded = false;
    public emailCodeExpired = false;
    public smsCodeExpired = false;

    constructor(data: ApiErrorParams) {
        super();
        this.message = 'ApiError';
        this.errors = data.errors;
        this.emailVerificationNeeded = data.emailVerificationNeeded || false;
        this.smsVerificationNeeded = data.smsVerificationNeeded || false;
        this.emailCodeExpired = data.emailCodeExpired || false;
        this.smsCodeExpired = data.smsCodeExpired || false;
    }
}

export default class ApiClient implements IApiClient {
    private client: AxiosInstance;

    protected createAxiosClient(baseUrl?: string): AxiosInstance {
        return Axios.create({
            baseURL: baseUrl,
            responseType: 'json' as const,
            headers: {
                'Content-Type': 'application/json',
                ...(window.GLOBALS.apiToken && {
                    Authorization: `Bearer ${window.GLOBALS.apiToken}`,
                }),
                ...(window.GLOBALS.language && {
                    'Accept-Language': window.GLOBALS.language,
                }),
            },
            timeout: 10 * 1000,
        });
    }

    constructor(baseUrl?: string) {
        this.client = this.createAxiosClient(baseUrl);
    }

    async post<TRequest, TResponse>(
        path: string,
        payload?: TRequest,
        config?: AxiosRequestConfig,
    ): Promise<ApiResponse<TResponse>> {
        const {
            data: {
                success,
                errors,
                emailVerificationNeeded,
                smsVerificationNeeded,
                emailCodeExpired,
                smsCodeExpired,
                ...response
            },
        } = await this.client.post<AxiosApiResponse<TResponse>>(path, payload, config);
        if (!success) {
            throw new ApiError({
                errors,
                emailVerificationNeeded,
                smsVerificationNeeded,
                emailCodeExpired,
                smsCodeExpired,
            });
        }
        return response;
    }

    async delete<TResponse>(path: string): Promise<ApiResponse<TResponse>> {
        const {
            data: {
                success,
                errors,
                emailVerificationNeeded,
                smsVerificationNeeded,
                emailCodeExpired,
                smsCodeExpired,
                ...response
            },
        } = await this.client.delete<AxiosApiResponse<TResponse>>(path);
        if (!success) {
            throw new ApiError({
                errors,
                emailVerificationNeeded,
                smsVerificationNeeded,
                emailCodeExpired,
                smsCodeExpired,
            });
        }
        return response;
    }

    async put<TRequest, TResponse>(path: string, payload: TRequest): Promise<ApiResponse<TResponse>> {
        const {
            data: {
                success,
                errors,
                emailVerificationNeeded,
                smsVerificationNeeded,
                emailCodeExpired,
                smsCodeExpired,
                ...response
            },
        } = await this.client.put<AxiosApiResponse<TResponse>>(path, payload);
        if (!success) {
            throw new ApiError({
                errors,
                emailVerificationNeeded,
                smsVerificationNeeded,
                emailCodeExpired,
                smsCodeExpired,
            });
        }
        return response;
    }

    async get<TResponse>(path: string, config?: AxiosRequestConfig): Promise<ApiResponse<TResponse>> {
        const {
            data: {
                success,
                errors,
                emailVerificationNeeded,
                smsVerificationNeeded,
                emailCodeExpired,
                smsCodeExpired,
                ...response
            },
        } = await this.client.get<AxiosApiResponse<TResponse>>(path, config);
        if (!success) {
            throw new ApiError({
                errors,
                emailVerificationNeeded,
                smsVerificationNeeded,
                emailCodeExpired,
                smsCodeExpired,
            });
        }
        return response;
    }
}
